import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';

const STORAGE_KEY = 'wmgra_cookie_consent';
const CONSENT_DURATION_MS = 6 * 30 * 24 * 60 * 60 * 1000; // ~6 months

const DEFAULT_PREFERENCES = {
  necessary: true,
  analytics: false,
  personalization: false,
  thirdParty: false,
};

function getStoredConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > CONSENT_DURATION_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function storeConsent(preferences) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    preferences,
    timestamp: Date.now(),
  }));
}

// Exported for footer "Gérer mes cookies" link
export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent('wmgra-open-cookie-prefs'));
}

/* SPRINT 8 (M20/M24) — textes du bandeau en fr/en (étaient en dur en FR). */
const COPY = {
  fr: {
    title: 'Gestion des cookies',
    intro: 'Ce site utilise des cookies pour assurer son bon fonctionnement et, avec votre accord, à des fins de mesure d\u2019audience, de personnalisation et de services tiers. ',
    more: 'En savoir plus',
    rejectAll: 'Tout refuser',
    acceptAll: 'Tout accepter',
    customize: 'Personnaliser',
    customizeMobile: 'Personnaliser mes choix',
    prefsTitle: 'Préférences cookies',
    back: 'Retour',
    alwaysOn: 'Toujours actif',
    save: 'Enregistrer mes préférences',
    retention: 'Votre choix sera conservé pendant 6 mois. Vous pouvez le modifier à tout moment via le lien « Gérer mes cookies » en pied de page. ',
    policy: 'Politique de cookies',
    on: 'activé', off: 'désactivé',
    categories: [
      { key: 'necessary', label: 'Cookies strictement nécessaires', description: 'Indispensables au fonctionnement du site : authentification, sécurité, session, stabilité technique. Toujours actifs.', locked: true },
      { key: 'analytics', label: 'Mesure d\u2019audience', description: 'Permettent de comprendre la fréquentation du site, mesurer les performances et améliorer l\u2019expérience utilisateur.', locked: false },
      { key: 'personalization', label: 'Personnalisation / confort', description: 'Mémorisent certaines préférences d\u2019affichage, de navigation ou de parcours non strictement nécessaires.', locked: false },
      { key: 'thirdParty', label: 'Services tiers', description: 'Liés à des services fournis par des prestataires externes : emails transactionnels, modules analytiques, intégrations de sécurité.', locked: false },
    ],
  },
  en: {
    title: 'Cookie management',
    intro: 'This site uses cookies to operate properly and, with your consent, for audience measurement, personalisation and third-party services. ',
    more: 'Learn more',
    rejectAll: 'Reject all',
    acceptAll: 'Accept all',
    customize: 'Customise',
    customizeMobile: 'Customise my choices',
    prefsTitle: 'Cookie preferences',
    back: 'Back',
    alwaysOn: 'Always on',
    save: 'Save my preferences',
    retention: 'Your choice is kept for 6 months. You can change it at any time via the “Manage my cookies” link in the footer. ',
    policy: 'Cookie policy',
    on: 'enabled', off: 'disabled',
    categories: [
      { key: 'necessary', label: 'Strictly necessary cookies', description: 'Essential to the operation of the site: authentication, security, session, technical stability. Always on.', locked: true },
      { key: 'analytics', label: 'Audience measurement', description: 'Help us understand traffic, measure performance and improve the experience.', locked: false },
      { key: 'personalization', label: 'Personalisation / comfort', description: 'Remember certain display, navigation or journey preferences that are not strictly necessary.', locked: false },
      { key: 'thirdParty', label: 'Third-party services', description: 'Related to services provided by external providers: transactional emails, analytics modules, security integrations.', locked: false },
    ],
  },
};

export default function CookieConsent() {
  const { lang } = useI18n();
  const cookiesPath = lang === 'fr' ? '/fr/politique-de-cookies' : '/en/cookie-policy';
  const s = COPY[lang === 'en' ? 'en' : 'fr'];

  // Do NOT show cookie consent on admin or portal (authenticated) pages
  const isAdminOrPortal = typeof window !== 'undefined' &&
    (window.location.pathname.startsWith('/admin') || window.location.pathname.startsWith('/portal'));

  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({ ...DEFAULT_PREFERENCES });

  useEffect(() => {
    if (isAdminOrPortal) return;
    const stored = getStoredConsent();
    if (!stored) {
      setVisible(true);
    } else {
      setPreferences(stored.preferences);
    }
  }, [isAdminOrPortal]);

  // Listen for "open preferences" event from footer link
  useEffect(() => {
    const handler = () => {
      const stored = getStoredConsent();
      if (stored) setPreferences(stored.preferences);
      else setPreferences({ ...DEFAULT_PREFERENCES });
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener('wmgra-open-cookie-prefs', handler);
    return () => window.removeEventListener('wmgra-open-cookie-prefs', handler);
  }, []);

  const handleAcceptAll = useCallback(() => {
    const all = { necessary: true, analytics: true, personalization: true, thirdParty: true };
    storeConsent(all);
    setPreferences(all);
    setVisible(false);
    setShowDetails(false);
  }, []);

  const handleRejectAll = useCallback(() => {
    const minimal = { ...DEFAULT_PREFERENCES };
    storeConsent(minimal);
    setPreferences(minimal);
    setVisible(false);
    setShowDetails(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    storeConsent(preferences);
    setVisible(false);
    setShowDetails(false);
  }, [preferences]);

  const toggleCategory = useCallback((key) => {
    if (key === 'necessary') return;
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  if (!visible) return null;

  const categories = s.categories;

  return (
    <>
      {/* Backdrop — purely visual, never blocks underlying interactions (Lot 351 fluidity fix). */}
      <div
        className="fixed inset-0 bg-charcoal/30 z-[9998] pointer-events-none transition-opacity duration-300"
        aria-hidden="true"
        data-testid="cookie-backdrop"
      />

      {/* Banner */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[9999] transition-transform duration-300"
        data-testid="cookie-consent-banner"
      >
        <div className="bg-ivory border-t border-stone shadow-elevated">
          <div className="max-w-5xl mx-auto px-6 py-6 md:py-8">

            {/* Compact view — mobile-first : texte resserré, 2 boutons en ligne
               + lien « Personnaliser » discret (PHASE 8 B7, viewport mobile) */}
            {!showDetails && (
              <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
                <div className="flex-1">
                  <p className="text-sm font-heading text-charcoal mb-1.5">{s.title}</p>
                  <p className="text-xs leading-relaxed text-graphite line-clamp-3 sm:line-clamp-none">
                    {s.intro}
                    <Link to={cookiesPath} className="text-gold-dark hover:underline" onClick={() => setVisible(false)}>{s.more}</Link>
                  </p>
                </div>
                <div className="flex flex-col gap-2 lg:flex-shrink-0">
                  <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3">
                    <button
                      onClick={handleRejectAll}
                      className="px-4 sm:px-5 py-2.5 text-xs font-medium border border-graphite/30 text-graphite hover:bg-graphite/5 transition-colors"
                      data-testid="cookie-reject-all"
                    >
                      {s.rejectAll}
                    </button>
                    <button
                      onClick={handleAcceptAll}
                      className="px-4 sm:px-5 py-2.5 text-xs font-medium bg-navy-deep text-ivory hover:bg-navy-deep/90 transition-colors"
                      data-testid="cookie-accept-all"
                    >
                      {s.acceptAll}
                    </button>
                    <button
                      onClick={() => setShowDetails(true)}
                      className="hidden sm:block px-5 py-2.5 text-xs font-medium border border-gold-dark/40 text-charcoal hover:bg-gold-wash transition-colors"
                      data-testid="cookie-customize"
                    >
                      {s.customize}
                    </button>
                  </div>
                  <button
                    onClick={() => setShowDetails(true)}
                    className="sm:hidden text-[11px] text-graphite underline underline-offset-2 text-center py-1"
                    data-testid="cookie-customize-mobile"
                  >
                    {s.customizeMobile}
                  </button>
                </div>
              </div>
            )}

            {/* Detail / preferences view */}
            {showDetails && (
              <div>
                <div className="flex items-center justify-between mb-5">
                  <p className="text-sm font-heading text-charcoal">{s.prefsTitle}</p>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="text-xs text-graphite hover:text-charcoal transition-colors"
                  >
                    {s.back}
                  </button>
                </div>

                <div className="space-y-4 mb-6 max-h-[40vh] overflow-y-auto pr-2">
                  {categories.map(cat => (
                    <div
                      key={cat.key}
                      className="flex items-start gap-4 py-3 border-b border-stone/50 last:border-b-0"
                      data-testid={`cookie-category-${cat.key}`}
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium text-charcoal mb-1">{cat.label}</p>
                        <p className="text-xs text-graphite leading-relaxed">{cat.description}</p>
                      </div>
                      <div className="flex-shrink-0 pt-1">
                        {cat.locked ? (
                          <span className="text-xs text-graphite/80 italic" data-testid={`cookie-toggle-${cat.key}`}>{s.alwaysOn}</span>
                        ) : (
                          <button
                            onClick={() => toggleCategory(cat.key)}
                            className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${preferences[cat.key] ? 'bg-gold-dark' : 'bg-graphite/25'}`}
                            data-testid={`cookie-toggle-${cat.key}`}
                            aria-label={`${cat.label} ${preferences[cat.key] ? s.on : s.off}`}
                          >
                            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${preferences[cat.key] ? 'translate-x-5' : 'translate-x-0'}`} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <button
                    onClick={handleRejectAll}
                    className="px-5 py-2.5 text-xs font-medium border border-graphite/30 text-graphite hover:bg-graphite/5 transition-colors"
                    data-testid="cookie-detail-reject"
                  >
                    {s.rejectAll}
                  </button>
                  <button
                    onClick={handleSavePreferences}
                    className="px-5 py-2.5 text-xs font-medium bg-navy-deep text-ivory hover:bg-navy-deep/90 transition-colors"
                    data-testid="cookie-save-preferences"
                  >
                    {s.save}
                  </button>
                </div>

                <p className="text-xs text-graphite/50 mt-4 text-center">
                  {s.retention}
                  <Link to={cookiesPath} className="text-gold-dark hover:underline" onClick={() => setVisible(false)}>{s.policy}</Link>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
